import api from './client'
import type {
  ProjectData, SCMInfo, ReferenceableProject, ProjectReference,
  ScmStatus, ScmBranches, ScmCommit, ScmCommitDetail, ScmPullResult, ScmChanges,
} from '@/types'

export async function getProjects(): Promise<ProjectData[]> {
  const { data } = await api.get('/workbench/projects')
  return data
}

export async function getProject(folderName: string): Promise<ProjectData> {
  const { data } = await api.get(`/workbench/projects/${encodeURIComponent(folderName)}`)
  return data
}

export async function saveDraft(projectId: string, sourceFolder?: string) {
  const { data } = await api.post('/workbench/save-draft', {
    project_id: projectId,
    ...(sourceFolder ? { source_folder: sourceFolder } : {}),
  })
  return data
}

export async function createBlankProject(projectId: string, title: string) {
  const { data } = await api.post('/workbench/create-blank-project', {
    project_id: projectId,
    title,
  })
  return data as { success: boolean; project_id: string }
}

/**
 * Download the project as a standalone runtime bundle (zip).
 *
 * Triggers a browser download with the project name. Throws on HTTP error;
 * caller is expected to surface the message via a toast. Returns the
 * runtime summary headers (db_mode / biz_logic / file_count) so the UI
 * can show a post-download confirmation.
 */
export async function downloadRuntimeBundle(folderName: string): Promise<{
  filename: string
  dbMode: string
  bizLogic: string
  fileCount: number
}> {
  const res = await api.get(
    `/workbench/projects/${encodeURIComponent(folderName)}/runtime-bundle`,
    { responseType: 'blob' },
  )
  const blob = res.data as Blob
  const filename = `${folderName}-runtime-bundle.zip`
  // Anchor + createObjectURL is the only cross-browser way to hand a Blob
  // to the download attribute. Revoke afterwards so we don't leak memory.
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
  return {
    filename,
    dbMode: String(res.headers['x-twasta-db-mode'] || ''),
    bizLogic: String(res.headers['x-twasta-biz-logic'] || ''),
    fileCount: Number(res.headers['x-twasta-file-count'] || 0),
  }
}

/**
 * Bundle multiple selected projects into a single zip. Triggers a browser
 * download with the timestamped filename the server picks. Throws on HTTP
 * error so the caller can surface the message in a toast.
 *
 * Returns the package summary (project count + entry count) so the wizard
 * can show a post-download confirmation.
 */
export type RuntimePackageFormat = 'tar.gz' | 'zip'

export async function downloadRuntimePackage(
  projectNames: string[],
  format: RuntimePackageFormat = 'tar.gz',
): Promise<{
  filename: string
  projectCount: number
  fileCount: number
  needsPostgres: boolean
  format: RuntimePackageFormat
}> {
  const res = await api.post(
    '/workbench/runtime-package',
    { project_names: projectNames, format },
    { responseType: 'blob' },
  )
  const blob = res.data as Blob
  // Extract filename from Content-Disposition; fall back to a sane default
  // if the header isn't present (e.g. CORS preflight stripped it).
  const cd = String(res.headers['content-disposition'] || '')
  const m = cd.match(/filename="([^"]+)"/i)
  const filename = m?.[1] || `twasta-runtime-package-${projectNames.length}apps.${format}`

  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
  return {
    filename,
    projectCount: Number(res.headers['x-twasta-project-count'] || projectNames.length),
    fileCount: Number(res.headers['x-twasta-file-count'] || 0),
    needsPostgres: String(res.headers['x-twasta-needs-postgres'] || '0') === '1',
    format: (String(res.headers['x-twasta-format'] || format) as RuntimePackageFormat),
  }
}

export interface DeleteDataTable {
  name: string
  rows: number
  /** true when `rows` is the database's estimate (a very large table) */
  rows_estimated: boolean
}

export interface DeleteDataExternalDb {
  kind: 'connection' | 'external_server' | 'unknown'
  name: string
  db_type: string
  database: string
  host: string
}

export interface DeleteDataPreview {
  own_database: boolean
  schema: string
  drop_schema: boolean
  /** designed tables the delete would drop (from the app's design) */
  tables: string[]
  /** of those, what exists in the database right now, with record counts */
  live_tables: DeleteDataTable[]
  /** set when the database could not be read — `tables` is then all we know */
  live_error: string | null
  external_database: DeleteDataExternalDb | null
  kept_shared: string[]
  other_organizations: number
  marketplace_installs: number
  error: string | null
  /** 'oracle' = a connected Oracle database: only tables Twasta created are dropped */
  dialect: 'postgres' | 'oracle'
  /** Oracle: designed tables Twasta did not create — always kept */
  kept_not_created: string[]
}

export async function getDeleteDataPreview(projectId: string): Promise<DeleteDataPreview> {
  const { data } = await api.get('/workbench/delete-application/preview', {
    params: { project_id: projectId },
  })
  return data
}

export interface DeleteApplicationResult {
  success: boolean
  message: string
  dropped: string[]
  notes: string[]
}

export async function deleteApplication(
  projectId: string, confirmationId: string, deleteData = false,
): Promise<DeleteApplicationResult> {
  const { data } = await api.post('/workbench/delete-application', {
    project_id: projectId,
    confirmation_id: confirmationId,
    delete_data: deleteData,
  })
  return data
}

export interface WorkspaceJunkReport {
  count: number
  removed: string[]
  freed_bytes: number
  dry_run: boolean
  errors?: { rel: string; error: string }[]
}

/** Preview stray/junk files in the project workspace (no deletion). */
export async function listWorkspaceJunk(folderName: string): Promise<WorkspaceJunkReport> {
  const { data } = await api.get(
    `/workbench/projects/${encodeURIComponent(folderName)}/workspace-junk`,
  )
  return data
}

/** Delete stray/junk files (rename backups, temp files, error dumps, agent
 *  scratch). Never touches design files or .twasta/model-backups/. */
export async function cleanWorkspaceJunk(folderName: string): Promise<WorkspaceJunkReport> {
  const { data } = await api.post(
    `/workbench/projects/${encodeURIComponent(folderName)}/workspace-junk/clean`,
  )
  return data
}

export async function getScmSettings() {
  const { data } = await api.get('/workbench/scm-settings')
  return data
}

export async function saveScmSettings(settings: Record<string, unknown>) {
  const { data } = await api.post('/workbench/scm-settings', settings)
  return data
}

export async function testScmConnection(connectionData: Record<string, unknown>) {
  const { data } = await api.post('/workbench/test-scm-connection', connectionData)
  return data
}

export async function testDbConnection(connectionData: {
  db_type: string
  host: string
  port: string
  username: string
  password: string
  database: string
  oracle_connect_type?: string
}) {
  const { data } = await api.post('/workbench/test-db-connection', connectionData)
  return data
}

export async function testLlmKey(provider: string, apiKey: string) {
  const { data } = await api.post('/workbench/test-llm-key', {
    provider,
    api_key: apiKey,
  })
  return data
}

export async function checkRepoAvailability(repoName: string) {
  const { data } = await api.post('/workbench/check-repository-availability', {
    repository_name: repoName,
  })
  return data
}

export async function getProjectScmInfo(projectId: string): Promise<SCMInfo> {
  const { data } = await api.get(`/workbench/project-scm-info/${encodeURIComponent(projectId)}`)
  return data
}

export async function pushToRepository(projectId: string, commitMessage?: string) {
  const { data } = await api.post('/workbench/push-to-repository', {
    project_id: projectId,
    commit_message: commitMessage || 'Update from Twasta.ai',
  })
  return data
}

export async function createProjectRepository(projectId: string) {
  const { data } = await api.post('/workbench/create-project-repository', {
    project_id: projectId,
  })
  return data
}

// The enterprise the profile's database is installed for, read from its
// MY_ENTERPRISE table. `editable` is true only for the "ALL" install, which is
// not pinned to one enterprise; every other install fixes the Enterprise field.
// A rejection here is the same condition that blocks the import.
export async function fetchDeployedEnterprise(profileId: number) {
  const { data } = await api.post('/workbench/deployed-enterprise', {
    profile_id: profileId,
  })
  return data as { success: boolean; enterprise: string; editable: boolean }
}

export async function scanDeployedApp(profileId: number, appName: string) {
  const { data } = await api.post('/workbench/scan-deployed-app', {
    profile_id: profileId,
    app_name: appName,
  })
  return data
}

// `enterprise` picks which copy of a report's JRXML/XSD to import: the deployed
// reports trees hold an enterprise's overrides in a folder named after it, and
// each file falls back to the common folder when it is not overridden there.
export async function importDeployedApp(
  profileId: number,
  appName: string,
  objects?: { obj_name: string; obj_type: string }[] | null,
  enterprise?: string,
) {
  const { data } = await api.post('/workbench/import-deployed-app', {
    profile_id: profileId,
    app_name: appName,
    // null/undefined = import all; [] = import the application blank.
    ...(objects ? { objects } : {}),
    ...(enterprise ? { enterprise } : {}),
  })
  return data
}

export async function validateGitRepository(repoName: string) {
  const { data } = await api.post('/workbench/validate-git-repository', {
    repository_name: repoName,
  })
  return data
}

export async function cloneGitRepository(repoName: string) {
  const { data } = await api.post('/workbench/clone-git-repository', {
    repository_name: repoName,
  })
  return data
}

// ---------------------------------------------------------------------------
// Full git lifecycle (Source Control tab)
// ---------------------------------------------------------------------------

export async function getScmStatus(projectId: string): Promise<ScmStatus> {
  const { data } = await api.get(`/workbench/scm/status/${encodeURIComponent(projectId)}`)
  return data
}

export async function getScmBranches(projectId: string): Promise<ScmBranches> {
  const { data } = await api.get(`/workbench/scm/branches/${encodeURIComponent(projectId)}`)
  return data
}

export async function getScmLog(
  projectId: string, limit = 50, skip = 0, paths: string[] = [],
): Promise<{ commits: ScmCommit[]; has_more: boolean }> {
  // `paths` limits the log to commits touching them (repeated ?path=).
  const params = new URLSearchParams({ limit: String(limit), skip: String(skip) })
  for (const p of paths) params.append('path', p)
  const { data } = await api.get(`/workbench/scm/log/${encodeURIComponent(projectId)}`, { params })
  return data
}

export async function getScmCommit(projectId: string, sha: string): Promise<ScmCommitDetail> {
  const { data } = await api.get(
    `/workbench/scm/commit/${encodeURIComponent(projectId)}/${encodeURIComponent(sha)}`,
  )
  return data
}

export async function getScmDiff(
  projectId: string, path: string, staged = false,
): Promise<{ path: string; staged: boolean; diff: string }> {
  const { data } = await api.get(`/workbench/scm/diff/${encodeURIComponent(projectId)}`, {
    params: { path, staged },
  })
  return data
}

export async function scmFetch(projectId: string) {
  const { data } = await api.post('/workbench/scm/fetch', { project_id: projectId })
  return data
}

export async function scmPull(
  projectId: string, strategy: 'merge' | 'rebase' = 'merge',
): Promise<ScmPullResult> {
  const { data } = await api.post('/workbench/scm/pull', { project_id: projectId, strategy })
  return data
}

export async function scmStage(projectId: string, paths: string[]) {
  const { data } = await api.post('/workbench/scm/stage', { project_id: projectId, paths })
  return data
}

export async function scmUnstage(projectId: string, paths: string[]) {
  const { data } = await api.post('/workbench/scm/unstage', { project_id: projectId, paths })
  return data
}

export async function scmDiscard(projectId: string, paths: string[]) {
  const { data } = await api.post('/workbench/scm/discard', { project_id: projectId, paths })
  return data
}

export async function scmCommit(
  projectId: string, message: string, paths?: string[], opts: { only?: boolean } = {},
) {
  // `only`: commit exactly `paths` (a version of one screen); anything else
  // already staged stays staged.
  const { data } = await api.post('/workbench/scm/commit', {
    project_id: projectId, message, ...(paths ? { paths } : {}), ...(opts.only ? { only: true } : {}),
  })
  return data
}

/** What is not saved as a version yet, per screen / report / menu. With an
 *  object, only its group (+ the menu, and the object's path `patterns`). */
export async function getScmChanges(
  projectId: string, obj?: { objType: string; objName: string },
): Promise<ScmChanges> {
  const { data } = await api.get(`/workbench/scm/changes/${encodeURIComponent(projectId)}`, {
    params: obj ? { obj_type: obj.objType, obj_name: obj.objName } : undefined,
  })
  return data
}

/** "Go back to this": the files a version changed (or `paths`) return to how
 *  they were in it, as unsaved changes. */
export async function scmRestoreVersion(projectId: string, sha: string, paths?: string[]) {
  const { data } = await api.post('/workbench/scm/restore', {
    project_id: projectId, sha, ...(paths ? { paths } : {}),
  })
  return data as { success: boolean; files: string[] }
}

export async function scmPush(projectId: string) {
  const { data } = await api.post('/workbench/scm/push', { project_id: projectId })
  return data
}

export async function scmCreateBranch(projectId: string, name: string, checkout = true) {
  const { data } = await api.post('/workbench/scm/branch/create', {
    project_id: projectId, name, checkout,
  })
  return data
}

export async function scmCheckoutBranch(projectId: string, name: string) {
  const { data } = await api.post('/workbench/scm/branch/checkout', { project_id: projectId, name })
  return data
}

export async function scmDeleteBranch(projectId: string, name: string, force = false) {
  const { data } = await api.post('/workbench/scm/branch/delete', {
    project_id: projectId, name, force,
  })
  return data
}

/** Settle conflicted files: mine = local version, theirs = pulled version,
 * manual = the file as edited in the code editor (refused while it still has
 * conflict markers). */
export async function scmResolveConflicts(projectId: string, paths: string[], side: 'mine' | 'theirs' | 'manual') {
  const { data } = await api.post('/workbench/scm/conflicts/resolve', { project_id: projectId, paths, side })
  return data
}

export async function scmAbortMerge(projectId: string) {
  const { data } = await api.post('/workbench/scm/merge/abort', { project_id: projectId })
  return data
}

export async function getReferenceableProjects(): Promise<ReferenceableProject[]> {
  const { data } = await api.get('/workbench/referenceable-projects')
  return data
}

export async function getProjectReferences(folderName: string): Promise<ProjectReference[]> {
  const { data } = await api.get(`/workbench/project-references/${encodeURIComponent(folderName)}`)
  return data
}

export async function setProjectReferences(
  projectId: string,
  references: Array<{ folder_name: string; owner_user_id: string }>
) {
  const { data } = await api.post('/workbench/project-references', {
    project_id: projectId,
    references,
  })
  return data
}

export async function refreshReference(
  projectId: string,
  refFolder: string,
  refOwnerId: string
) {
  const { data } = await api.post('/workbench/refresh-reference', {
    project_id: projectId,
    ref_folder: refFolder,
    ref_owner_id: refOwnerId,
  })
  return data
}
